import React from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BulkUploadResumeStatus } from '@/utils/bulkUploadResume'

type BulkUploadResumeSummaryProps = {
  loading?: boolean
  error?: boolean
  resume?: BulkUploadResumeStatus | null
  onRetry?: () => void
}

export const BulkUploadResumeSummary: React.FC<BulkUploadResumeSummaryProps> = ({
  loading = false,
  error = false,
  resume,
  onRetry,
}) => {
  const { t } = useTranslation(['BulkPodUpload', 'Common'])

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        {t('BulkPodUpload:messages.checkingProcessed', 'Checking which files are already processed…')}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <span className="text-destructive">
          {t('BulkPodUpload:messages.processedCheckFailed', 'Could not check processed files')}
        </span>
        {onRetry ? (
          <button type="button" className="text-sm font-medium text-primary hover:underline" onClick={onRetry}>
            {t('Common:general.retry', 'Retry')}
          </button>
        ) : null}
      </div>
    )
  }

  if (!resume) return null

  const remaining = resume.counts.new + resume.counts.retry
  const items = [
    {
      key: 'done',
      label: t('BulkPodUpload:resume.done', 'Already processed'),
      value: resume.counts.done,
      className: 'text-emerald-700 dark:text-emerald-400',
    },
    {
      key: 'remaining',
      label: t('BulkPodUpload:resume.remaining', 'To upload'),
      value: remaining,
      className: 'text-primary',
    },
    {
      key: 'retry',
      label: t('BulkPodUpload:resume.retry', 'Retry'),
      value: resume.counts.retry,
      className: 'text-amber-700 dark:text-amber-400',
    },
    {
      key: 'new',
      label: t('BulkPodUpload:resume.new', 'New'),
      value: resume.counts.new,
      className: 'text-muted-foreground',
    },
  ]

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {items.map(item => (
          <div key={item.key}>
            <div className="text-xs text-muted-foreground">{item.label}</div>
            <div className={`mt-0.5 text-lg font-semibold tabular-nums ${item.className}`}>{item.value}</div>
          </div>
        ))}
      </div>
      {resume.counts.done > 0 && remaining > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {t(
            'BulkPodUpload:resume.hint',
            'Re-running upload will skip processed files and continue with the rest.'
          )}
        </p>
      ) : null}
    </div>
  )
}

export default BulkUploadResumeSummary
